"use client";

import { useEffect, useState } from "react";
import { CircleOff } from "lucide-react";
import { AreaSeriesChart, PerformanceBars, SkewChart, VolTermChart } from "./charts";
import { MacroChart } from "./macro-chart";
import { FreshnessTag, Panel, PanelHeader, PeriodTabs, StatCard } from "./ui";
import { derivedSpreadToMacroSeries } from "@/lib/macro";
import type { DashboardData, MacroSeries, Tone } from "@/lib/types";
import { loadMarketsPeriod, saveMarketsPeriod } from "@/lib/view-preferences";

export function SectionIntro({ id, eyebrow, title, description }: { id: string; eyebrow: string; title: string; description: string }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--hc-muted)]">{eyebrow}</p>
      <h2 id={id} className="mt-1 text-xl font-semibold tracking-[-0.025em] text-[var(--hc-text)]">{title}</h2>
      <p className="mt-1 max-w-4xl text-sm leading-6 text-[var(--hc-muted)]">{description}</p>
    </div>
  );
}

export function EmptyState({ children = "No validated observations are available." }: { children?: React.ReactNode }) {
  return (
    <div className="grid min-h-40 place-items-center rounded-md border border-dashed border-[var(--hc-border-strong)] bg-[var(--hc-bg)] p-4 text-center">
      <div>
        <CircleOff className="mx-auto text-[var(--hc-subtle)]" size={22} aria-hidden="true" />
        <p className="mt-2 text-sm text-[var(--hc-muted)]">{children}</p>
      </div>
    </div>
  );
}

function availableSeries(record: Record<string, MacroSeries> | undefined, ...keys: string[]) {
  return keys.flatMap((key) => record?.[key] ? [record[key]] : []);
}

export function OverviewPanel({ data }: { data: DashboardData }) {
  const oecdCli = data.manufacturing.series.find((series) => series.key === "us_oecd_cli");
  const oecdCliPoint = oecdCli?.points.at(-1);
  const btc = data.crypto.btc_spot?.at(-1);
  const run = data.collector.run;

  const metrics: Array<{ label: string; value: string; detail: string; tone: Tone }> = [
    { label: "US OECD CLI", value: oecdCliPoint?.value.toFixed(2) ?? "—", detail: oecdCliPoint?.date ?? "No validated observation", tone: data.manufacturing.oecd_source?.status === "live" ? "positive" : "neutral" },
    { label: "BTC Spot", value: btc ? `$${btc.value.toLocaleString("en", { maximumFractionDigits: 0 })}` : "—", detail: btc?.date ?? "No validated observation", tone: "neutral" },
    { label: "Collector Health", value: run?.state?.toUpperCase() ?? "NO RUN", detail: run?.finished_at ?? "Collector status not reported", tone: run?.state === "success" ? "positive" : run?.state === "running" ? "warning" : run ? "danger" : "neutral" }
  ];

  return (
    <section id="overview" aria-labelledby="overview-heading" className="space-y-4">
      <SectionIntro id="overview-heading" eyebrow="Global view" title="Overview" description="Public OECD, BTC market data, and collector health at a glance." />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {metrics.map((metric) => <StatCard key={metric.label} {...metric} />)}
      </div>
    </section>
  );
}

export function ManufacturingPanel({ data }: { data: DashboardData["manufacturing"] }) {
  const macro = data.macro?.series;
  const spread = data.macro?.new_orders_minus_inventories;
  const spreadSources = availableSeries(macro, "us_ism_new_orders", "us_ism_inventories");

  return (
    <section id="manufacturing" aria-labelledby="manufacturing-heading" className="space-y-4">
      <SectionIntro id="manufacturing-heading" eyebrow="Public macro" title="Manufacturing" description="Free Composite Leading Indicator observations from the public OECD SDMX endpoint. No account or paid data entitlement is required." />
      {data.series.length ? data.series.map((series) => (
        <Panel key={series.key}>
          <PanelHeader title={series.label} meta="OECD · public web data">
            {data.oecd_source ? <FreshnessTag status={data.oecd_source.status} source={data.oecd_source.name} collectedAt={data.oecd_source.collected_at} /> : undefined}
          </PanelHeader>
          <AreaSeriesChart points={series.points} color="#43d6a0" height={300} />
        </Panel>
      )) : <Panel><PanelHeader title="US OECD Composite Leading Indicator" meta="OECD · public web data" /><EmptyState /></Panel>}
      {macro ? (
        <>
          <Panel className="min-h-[360px]">
            <MacroChart title="MM Manufacturing Cycle" description="Primary cycle signal. Source observations are shown without interpolation or forward fill." series={availableSeries(macro, "mm_manufacturing_cycle")} height={340} zeroLine />
          </Panel>
          <div className="grid gap-4 xl:grid-cols-2">
            <Panel><MacroChart title="ISM New Orders and Manufacturing PMI" description="New Orders uses its configured one-month display shift; PMI remains at observation date." series={availableSeries(macro, "us_ism_new_orders", "us_ism_pmi")} /></Panel>
            <Panel><MacroChart title="New Orders minus Inventories" description="Formula: US ISM New Orders − US ISM Inventories. Displayed with a +2 month analytical lead." series={spread ? [derivedSpreadToMacroSeries(spread)] : []} sourceSeries={spreadSources} zeroLine /></Panel>
            <Panel><MacroChart title="China Credit Impulse" description="China Bloomberg Credit Impulse with its approved +6 month display shift." series={availableSeries(macro, "china_credit_impulse")} zeroLine /></Panel>
            <Panel><MacroChart title="OECD CLI YoY Diffusion" description="Share of OECD leading indicators improving year over year. Missing releases remain visible as gaps." series={availableSeries(macro, "oecd_cli_yoy_diffusion")} /></Panel>
            <Panel><MacroChart title="World PMI YoY Diffusion" description="Share of the world PMI breadth universe improving year over year." series={availableSeries(macro, "world_pmi_yoy_diffusion")} /></Panel>
          </div>
        </>
      ) : null}
    </section>
  );
}

export function MarketsPanel({ data }: { data: DashboardData["markets"] }) {
  const [period, setPeriod] = useState(data.periods[0] ?? "1D");
  const periodsKey = data.periods.join("\u0000");

  useEffect(() => {
    const restored = loadMarketsPeriod(window.localStorage, data.periods);
    setPeriod((current) => restored ?? (data.periods.includes(current) ? current : data.periods[0] ?? "1D"));
  }, [data.periods, periodsKey]);

  const selectPeriod = (nextPeriod: string) => {
    setPeriod(nextPeriod);
    saveMarketsPeriod(window.localStorage, nextPeriod, data.periods);
  };
  const fomcHawkishDovish = data.fomc.series.map((point) => ({ date: point.date, value: point.hawkish_dovish }));
  const fomcOptimism = data.fomc.series.map((point) => ({ date: point.date, value: point.optimism }));
  const pbocSeries = availableSeries(
    data.pboc_balance_sheet_assets?.series,
    "pboc_total_assets",
    "pboc_funds_outstanding_for_forex",
    "pboc_claims_on_depository_corporations"
  );

  return (
    <section id="markets" aria-labelledby="markets-heading" className="space-y-4">
      <SectionIntro id="markets-heading" eyebrow="Cross-asset" title="Markets" description="Public Yahoo cross-asset performance, official Federal Reserve statement tone, and China balance-sheet context." />
      <Panel>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <PanelHeader title="ETF / Asset Performance" meta={period} />
          <div className="flex flex-wrap items-center gap-2">
            <FreshnessTag status={data.performance_source?.status} source={data.performance_source?.name} collectedAt={data.performance_source?.collected_at} />
            <PeriodTabs periods={data.periods} value={period} onChange={selectPeriod} />
          </div>
        </div>
        {data.performance.length ? <PerformanceBars data={data.performance} period={period} /> : <EmptyState />}
      </Panel>
      <Panel>
        <PanelHeader title="FOMC Statement Tone" meta="Federal Reserve · public statements">
          {data.fomc_source ? <FreshnessTag status={data.fomc_source.status} source={data.fomc_source.name} collectedAt={data.fomc_source.collected_at} /> : undefined}
        </PanelHeader>
        {data.fomc.series.length ? (
          <div className="grid gap-4 xl:grid-cols-2">
            <div><p className="mb-2 text-xs font-semibold text-[var(--hc-muted)]">Hawkish–Dovish</p><AreaSeriesChart points={fomcHawkishDovish} color="#49bff2" /></div>
            <div><p className="mb-2 text-xs font-semibold text-[var(--hc-muted)]">Optimism</p><AreaSeriesChart points={fomcOptimism} color="#a58af8" /></div>
          </div>
        ) : <EmptyState />}
      </Panel>
      <Panel className="min-h-[380px]">
        <MacroChart
          title="China — PBoC Balance Sheet Assets"
          description="Major asset components of the People's Bank of China balance sheet. Values are reported in CNY 100 million; observations are collected from the public MacroMicro chart without interpolation."
          series={pbocSeries}
          height={360}
          valueFormatter={(value) => new Intl.NumberFormat("en", { maximumFractionDigits: 2 }).format(value)}
        />
      </Panel>
    </section>
  );
}

export function CryptoPanel({ data }: { data: DashboardData["crypto"] }) {
  return (
    <section id="crypto" aria-labelledby="crypto-heading" className="space-y-4">
      <SectionIntro id="crypto-heading" eyebrow="Digital asset context" title="Bitcoin derivatives" description="BTC spot, aggregate futures positioning, and the Deribit volatility surface kept alongside the macro dashboard." />
      <Panel>
        <PanelHeader title="BTC Spot" meta={data.btc_spot_source?.name ?? "source unavailable"}>
          {data.btc_spot_source?.status ? <FreshnessTag status={data.btc_spot_source.status} source={data.btc_spot_source.name} collectedAt={data.btc_spot_source.collected_at} /> : undefined}
        </PanelHeader>
        {data.btc_spot?.length ? <AreaSeriesChart points={data.btc_spot} color="#f7c05a" height={220} /> : <EmptyState />}
      </Panel>
      {data.open_interest.map((series) => (
        <Panel key={series.key}>
          <PanelHeader title={series.label} meta={data.oi_source?.name ?? "CoinGlass"}>
            {data.oi_source?.status ? <FreshnessTag status={data.oi_source.status} source={data.oi_source.name} collectedAt={data.oi_source.collected_at} /> : undefined}
          </PanelHeader>
          {series.points.length ? <AreaSeriesChart points={series.points} color="#49bff2" height={240} /> : <EmptyState />}
        </Panel>
      ))}
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel><PanelHeader title={data.vol_term_structure.label} meta={data.vol_term_structure.source_status?.name ?? "Deribit"}>{data.vol_term_structure.source_status?.status ? <FreshnessTag status={data.vol_term_structure.source_status.status} source={data.vol_term_structure.source_status.name ?? "Deribit"} collectedAt={data.vol_term_structure.source_status.collected_at} /> : undefined}</PanelHeader>{data.vol_term_structure.tenors.length ? <VolTermChart data={data.vol_term_structure.tenors} /> : <EmptyState />}</Panel>
        <Panel><PanelHeader title={data.skew_25d.label} meta={data.skew_25d.source_status?.name ?? "Deribit"}>{data.skew_25d.source_status?.status ? <FreshnessTag status={data.skew_25d.source_status.status} source={data.skew_25d.source_status.name ?? "Deribit"} collectedAt={data.skew_25d.source_status.collected_at} /> : undefined}</PanelHeader>{data.skew_25d.tenors.length ? <SkewChart data={data.skew_25d.tenors} /> : <EmptyState />}</Panel>
      </div>
    </section>
  );
}
